import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const topics = ["投稿神翻译", "避雷举报", "翻译对比建议", "合作咨询", "其他"];

export default function Contact() {
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get("contact_alt") as string) || "";

    setFormError("");
    setFormSuccess(false);

    // 反垃圾蜜罐：命中则伪装成成功，不提交
    if (honeypot.trim()) {
      setFormSuccess(true);
      return;
    }

    // 构建 payload（排除蜜罐字段）
    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key !== "contact_alt" && typeof value === "string") payload[key] = value;
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/da237hbk8r5te5mqsf9g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || responseText;

      const code = parsed?.code as string;
      const isOk = res.ok && code === "OK";

      if (isOk) {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setFormError("网络异常，请检查后重试。");
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-32 pb-16">
        <nav aria-label="面包屑" className="text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
            <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
            <li aria-current="page" className="text-foreground-800">联系我们</li>
          </ol>
        </nav>

        <header className="mt-6 max-w-3xl">
          <p className="font-label text-sm text-primary-600">CONTACT</p>
          <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-5xl">联系我们</h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            想投稿神翻译、避雷举报、提翻译对比建议，或是有合作意向？都可以通过下面的表单发给我们，
            经审核后我们会标注来源收录进对应栏目。
          </p>
        </header>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* 联系信息 */}
          <aside className="flex flex-col gap-5 lg:col-span-4">
            <div className="rounded-xl bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  paozha@example.com
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                    <i className="ri-chat-3-line" aria-hidden="true"></i>
                  </span>
                  站内投稿表单（下方）
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  通常 2–3 个工作日内回复
                </li>
              </ul>
            </div>

            <div className="rounded-xl bg-background-100 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">投稿小贴士</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                投稿神翻译或避雷时，最好附上作品名、章节号，以及你觉得译得妙 / 译得烂的具体台词或分镜。
                证据越具体，越容易通过审核被收录。
              </p>
            </div>
          </aside>

          {/* 表单 */}
          <div className="lg:col-span-8">
            <form
              data-readdy-form="paozha-feedback"
              onSubmit={handleSubmit}
              className="rounded-xl bg-background-50 p-6 ring-1 ring-background-200 md:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground-900">称呼</span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="怎么称呼你"
                    className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-foreground-900">邮箱</span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收回复"
                    className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </label>
              </div>

              <label className="mt-5 flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground-900">投稿类型</span>
                <select
                  name="topic"
                  required
                  className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  {topics.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>

              <label className="mt-5 flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground-900">作品与章节</span>
                <input
                  type="text"
                  name="work"
                  placeholder="如：街角的面包房 · 第 8 话"
                  className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>

              <label className="mt-5 flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground-900">详细内容</span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请具体描述你的投稿、吐槽或建议（不超过 500 字）"
                  className="resize-none rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
                <span className="text-xs text-foreground-400">最多 500 字</span>
              </label>

              {/* 反垃圾蜜罐字段 */}
              <div className="pz-honeypot">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formError && (
                <div className="mt-5 flex items-center gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                  <span>{formError}</span>
                </div>
              )}
              {formSuccess && (
                <div className="mt-5 flex items-center gap-2 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  <span>收到啦！感谢你的投稿，我们会尽快处理。</span>
                </div>
              )}

              <button
                type="submit"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600"
              >
                提交投稿
                <i className="ri-send-plane-line" aria-hidden="true"></i>
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}